import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { AppRole, UserRolesResponse } from '../../generated/api.onboarding.client';
import { createApiClient } from '../api/apiClient';
import { isTestMode } from '../../config';
import { getMockUserRolesResponse } from './mockPrehireData';

interface UseUserRolesResult {
    roles: AppRole[];
    userEmail: string | undefined;
    workOnBehalfHiringManagerEmails: string[];
    loading: boolean;
    error: string | null;
}

/**
 * Fetches the authenticated user's roles via GET /api/Roles/me.
 */
export const useUserRoles = (): UseUserRolesResult => {
    const msalContext = useMsal();
    const instance = isTestMode ? null : msalContext?.instance;
    const accounts = isTestMode ? [] : msalContext?.accounts || [];
    const account = accounts[0] || null;

    const [roles, setRoles] = useState<AppRole[]>([]);
    const [userEmail, setUserEmail] = useState<string | undefined>();
    const [workOnBehalfHiringManagerEmails, setWorkOnBehalfHiringManagerEmails] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchRoles = useCallback(async () => {
        // In test mode, return mock roles immediately (includes WorkOnBehalf)
        if (isTestMode) {
            const mock = getMockUserRolesResponse();
            setUserEmail(mock.email ?? undefined);
            const uniqueRoles = Array.from(
                new Set((mock.roles ?? []).map(r => r.role))
            );
            setRoles(uniqueRoles);
            const wobEmails = Array.from(
                new Set(
                    (mock.roles ?? [])
                        .filter(r => r.role === AppRole.WorkOnBehalf)
                        .flatMap(r => r.hiringManagerEmails ?? [])
                        .map(email => email.trim())
                        .filter(Boolean),
                ),
            );
            setWorkOnBehalfHiringManagerEmails(wobEmails);
            setLoading(false);
            return;
        }

        if (!isTestMode && !account) {
            setLoading(false);
            setError('No authenticated account found');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const client = createApiClient(instance, account);
            const result: UserRolesResponse = await client.me();
            setUserEmail(result.email ?? undefined);
            const uniqueRoles = Array.from(
                new Set((result.roles ?? []).map(r => r.role))
            );
            setRoles(uniqueRoles);
            const wobEmails = Array.from(
                new Set(
                    (result.roles ?? [])
                        .filter(r => r.role === AppRole.WorkOnBehalf)
                        .flatMap(r => r.hiringManagerEmails ?? [])
                        .map(email => email.trim())
                        .filter(Boolean),
                ),
            );
            setWorkOnBehalfHiringManagerEmails(wobEmails);
        } catch (err: unknown) {
            console.error('Error fetching user roles:', err);
            setError('Failed to load user roles.');
        } finally {
            setLoading(false);
        }
    }, [instance, account]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    return { roles, userEmail, workOnBehalfHiringManagerEmails, loading, error };
};
